import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import './SoulView.css';

// ── Dimension metadata (same as SoulOrb) ──
const DIMENSIONS = [
  { key: 'identity',            label: 'Identity',          color: '#E8A87C', icon: '◉' },
  { key: 'values_beliefs',      label: 'Values & Beliefs',  color: '#85CDCA', icon: '◈' },
  { key: 'personality',         label: 'Personality',        color: '#D4A5FF', icon: '◇' },
  { key: 'emotional_landscape', label: 'Emotional',         color: '#FF6B6B', icon: '♡' },
  { key: 'relationships',       label: 'Relationships',     color: '#4ECDC4', icon: '∞' },
  { key: 'life_story',          label: 'Life Story',        color: '#FFE66D', icon: '☆' },
  { key: 'wisdom_perspective',  label: 'Wisdom',            color: '#A8E6CF', icon: '✧' },
  { key: 'voice_expression',    label: 'Expression',        color: '#FF8A5C', icon: '♪' },
];

// ── Core Particle Sphere (dense point cloud orb) ──
function CoreSphere({ overallScore, isSpeaking }) {
  const meshRef = useRef();
  const particleCount = 3000;

  const { positions, basePositions, sizes } = useMemo(() => {
    const pos = new Float32Array(particleCount * 3);
    const base = new Float32Array(particleCount * 3);
    const sz = new Float32Array(particleCount);
    for (let i = 0; i < particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      // Gaussian-ish distribution: most particles near surface, some inside
      const u = Math.random();
      const r = 0.3 + u * u * 0.75; // bias toward outer shell
      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.sin(phi) * Math.sin(theta);
      const z = r * Math.cos(phi);
      pos[i * 3] = x;
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = z;
      base[i * 3] = x;
      base[i * 3 + 1] = y;
      base[i * 3 + 2] = z;
      // Smaller particles at center, larger near surface
      sz[i] = 0.008 + r * 0.022;
    }
    return { positions: pos, basePositions: base, sizes: sz };
  }, []);

  const colors = useMemo(() => {
    const col = new Float32Array(particleCount * 3);
    const teal = new THREE.Color('#3b725f');
    const mint = new THREE.Color('#85CDCA');
    const white = new THREE.Color('#c8e6df');
    for (let i = 0; i < particleCount; i++) {
      const r = Math.sqrt(
        basePositions[i * 3] ** 2 + basePositions[i * 3 + 1] ** 2 + basePositions[i * 3 + 2] ** 2
      );
      const t = Math.min(r / 1.05, 1);
      const brightness = 0.4 + overallScore * 0.6;
      // Inner particles are white-ish, outer particles teal/mint
      const c = t < 0.5
        ? white.clone().lerp(mint, t * 2)
        : mint.clone().lerp(teal, (t - 0.5) * 2);
      col[i * 3] = c.r * brightness;
      col[i * 3 + 1] = c.g * brightness;
      col[i * 3 + 2] = c.b * brightness;
    }
    return col;
  }, [overallScore, basePositions]);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;

    meshRef.current.rotation.y += 0.0008;
    meshRef.current.rotation.x = Math.sin(t * 0.2) * 0.03;

    const posAttr = meshRef.current.geometry.attributes.position;
    if (!posAttr) return;

    if (isSpeaking) {
      const speakPulse = 1.0 + Math.sin(t * 6) * 0.06 + Math.sin(t * 9.5) * 0.04;
      for (let i = 0; i < particleCount; i++) {
        const jitter = Math.sin(t * 8 + i * 0.5) * 0.015;
        posAttr.array[i * 3] = basePositions[i * 3] * (speakPulse + jitter);
        posAttr.array[i * 3 + 1] = basePositions[i * 3 + 1] * (speakPulse + jitter);
        posAttr.array[i * 3 + 2] = basePositions[i * 3 + 2] * (speakPulse + jitter);
      }
      posAttr.needsUpdate = true;
      meshRef.current.scale.setScalar(1.0 + Math.sin(t * 5) * 0.04);
    } else {
      // Subtle idle drift — particles breathe gently
      const pulse = 1.0 + Math.sin(t * 0.6) * 0.015;
      for (let i = 0; i < particleCount; i++) {
        const drift = Math.sin(t * 0.4 + i * 0.1) * 0.008;
        posAttr.array[i * 3] = basePositions[i * 3] * (pulse + drift);
        posAttr.array[i * 3 + 1] = basePositions[i * 3 + 1] * (pulse + drift * 0.5);
        posAttr.array[i * 3 + 2] = basePositions[i * 3 + 2] * (pulse + drift);
      }
      posAttr.needsUpdate = true;
      meshRef.current.scale.setScalar(1);
    }
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={particleCount} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-color" count={particleCount} array={colors} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial
        size={0.02}
        vertexColors
        transparent
        opacity={0.7 + overallScore * 0.3}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

// ── Ambient Field (sparse floating particles around the orb) ──
function AmbientField({ overallScore, isSpeaking }) {
  const meshRef = useRef();
  const particleCount = 400;

  const { positions, basePositions } = useMemo(() => {
    const pos = new Float32Array(particleCount * 3);
    const base = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      // Sparse field between r=1.3 and r=3.0
      const r = 1.3 + Math.random() * 1.7;
      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.sin(phi) * Math.sin(theta);
      const z = r * Math.cos(phi);
      pos[i * 3] = x;
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = z;
      base[i * 3] = x;
      base[i * 3 + 1] = y;
      base[i * 3 + 2] = z;
    }
    return { positions: pos, basePositions: base };
  }, []);

  const colors = useMemo(() => {
    const col = new Float32Array(particleCount * 3);
    const teal = new THREE.Color('#3b725f');
    for (let i = 0; i < particleCount; i++) {
      const r = Math.sqrt(
        basePositions[i * 3] ** 2 + basePositions[i * 3 + 1] ** 2 + basePositions[i * 3 + 2] ** 2
      );
      // Fade out with distance
      const fade = Math.max(0, 1 - (r - 1.3) / 1.7) * 0.6;
      col[i * 3] = teal.r * fade;
      col[i * 3 + 1] = teal.g * fade;
      col[i * 3 + 2] = teal.b * fade;
    }
    return col;
  }, [basePositions]);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;
    meshRef.current.rotation.y += 0.0003;

    const posAttr = meshRef.current.geometry.attributes.position;
    if (!posAttr) return;

    // Gentle individual drift
    for (let i = 0; i < particleCount; i++) {
      const speed = isSpeaking ? 1.5 : 0.3;
      const dx = Math.sin(t * speed + i * 1.7) * 0.01;
      const dy = Math.cos(t * speed * 0.7 + i * 2.3) * 0.01;
      const dz = Math.sin(t * speed * 0.5 + i * 3.1) * 0.01;
      posAttr.array[i * 3] = basePositions[i * 3] + dx;
      posAttr.array[i * 3 + 1] = basePositions[i * 3 + 1] + dy;
      posAttr.array[i * 3 + 2] = basePositions[i * 3 + 2] + dz;
    }
    posAttr.needsUpdate = true;
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={particleCount} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-color" count={particleCount} array={colors} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial
        size={0.012}
        vertexColors
        transparent
        opacity={0.25 + overallScore * 0.2}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

// ── Inner Glow ──
function InnerGlow({ overallScore, isSpeaking }) {
  const meshRef = useRef();

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;
    if (isSpeaking) {
      const pulse = 0.85 + Math.sin(t * 5) * 0.15 + Math.sin(t * 8.5) * 0.08;
      meshRef.current.scale.setScalar(pulse);
      meshRef.current.material.opacity = 0.08 + overallScore * 0.18 + Math.sin(t * 6) * 0.06;
    } else {
      const pulse = 0.8 + Math.sin(t * 0.5) * 0.1;
      meshRef.current.scale.setScalar(pulse);
      meshRef.current.material.opacity = 0.05 + overallScore * 0.12;
    }
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[1.1, 32, 32]} />
      <meshBasicMaterial
        color="#3b725f"
        transparent
        opacity={0.08}
        side={THREE.BackSide}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

// ── Full 3D Scene ──
function SoulScene({ dimensions, overallScore, isSpeaking }) {
  return (
    <>
      <ambientLight intensity={0.1} />
      <pointLight position={[0, 0, 0]} intensity={0.4 + overallScore * 1.0} color="#3b725f" />
      <OrbitControls
        enableDamping
        dampingFactor={0.08}
        minDistance={2}
        maxDistance={8}
        enablePan={false}
        autoRotate
        autoRotateSpeed={0.2}
      />
      <InnerGlow overallScore={overallScore} isSpeaking={isSpeaking} />
      <CoreSphere overallScore={overallScore} isSpeaking={isSpeaking} />
      <AmbientField overallScore={overallScore} isSpeaking={isSpeaking} />
    </>
  );
}

// ── Exported SoulView Component ──
export default function SoulView({ token, api, isSpeaking = false }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${api}/soul_profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      }
    } catch (e) {
      console.error('Failed to fetch soul profile:', e);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const overallScore = profile?.overall_score || 0;
  const dimensions = profile?.dimensions || {};

  // Compute depth from evidence_count for each dimension (fallback if backend doesn't provide it)
  const computeDepth = (count) => count > 0 ? Math.round(Math.log2(1 + count * 1.5) * 10) / 10 : 0;

  // Find shallowest dimensions to suggest exploration
  const dimDepths = DIMENSIONS.map(d => ({
    ...d,
    depth: dimensions[d.key]?.depth ?? computeDepth(dimensions[d.key]?.evidence_count || 0),
    evidence: dimensions[d.key]?.evidence_count || 0,
  })).sort((a, b) => a.depth - b.depth);
  const maxDepth = Math.max(...dimDepths.map(d => d.depth), 1);

  // Overall depth: use backend value or compute as sum of dimension depths
  const overallDepth = profile?.overall_depth || Math.round(dimDepths.reduce((s, d) => s + d.depth, 0) * 10) / 10;

  if (loading) {
    return (
      <div className="sv-root">
        <div className="sv-loading">
          <div className="sv-loading-orb" />
          <span>Loading soul...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="sv-root">
      <Canvas
        camera={{ position: [0, 0, 3.5], fov: 50 }}
        gl={{ antialias: true, alpha: true }}
        dpr={[1, 1.5]}
        style={{ background: 'radial-gradient(ellipse at center, #0f1914 0%, #080c0a 100%)' }}
      >
        <SoulScene dimensions={dimensions} overallScore={overallScore} isSpeaking={isSpeaking} />
      </Canvas>

      {/* Depth overlay */}
      <div className="sv-score-overlay">
        <div className="sv-score-value">{Math.round(overallDepth)}</div>
        <div className="sv-score-label">Soul Depth</div>
      </div>

      {/* Speaking indicator */}
      {isSpeaking && (
        <div className="sv-speaking-indicator">
          <div className="sv-speaking-dot" />
          <span>Listening...</span>
        </div>
      )}
    </div>
  );
}
